import { EXECUTIVE_SUMMARY_TRACEABILITY_FINDING } from '../audit/reviewedExecutiveSummary';
import { withWaterSourceTruthPolicy } from './waterSourcePolicy';
import { withAirAttainmentTruthPolicy } from './airAttainmentPolicy';
import {
  regulatoryFeasibilityDecisionRows,
  regulatoryFeasibilityPolicyApplies,
  withRegulatoryFeasibilityPolicy,
} from './regulatoryFeasibilityPolicy';
import { withCurrentTruthReadinessPolicy } from './currentTruthReadinessPolicy';
import type { TestFitAuthorityRef } from '../../types/generationImpact';
import type {
  DecisionAnalysisDomain,
  DecisionAnalysisFindingV1,
  DecisionAnalysisRiskV1,
  DecisionAnalysisSemanticContentV1,
  DecisionAnalysisSnapshotV1,
  DecisionAnalysisUpstreamReferenceV1,
  DecisionAnalysisVersionEnvelopeV1,
} from '../../types/decisionAnalysis';
import {
  decisionAnalysisDomainSchema,
  decisionAnalysisRiskV1Schema,
} from '../../types/decisionAnalysis';
import type {
  EvidenceValue,
  JsonValue,
  ReportDocumentModelV1,
} from '../../types/reportContractsV2';
import { reportDocumentModelV1Schema } from '../../types/reportContractsV2';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type {
  ProjectIntent,
  SanityFinding,
  SiteRagReport,
  TypedPreferencesByType,
} from '../../types/siteRag';
import {
  collectNarrativeStrings,
  isFalsePositiveQueueDurationFindingText,
} from '../audit/queueDurationCheck';
import { isReviewIssueAlreadyQualifiedInText } from '../audit/reviewIssueSurvival';
import { sanityFindingIdentity } from '../reportUserState';
import { resolveCanonicalReportDecisionProjection } from '../resolveEffectiveConclusion';
import { hashCanonicalJson } from '../reportGeneration/reportContractHash';
import {
  WITHHELD_NARRATIVE_NOTICE,
  formatReportLeafPath,
  withheldNarrativeLeaves,
} from '../report/withheldNarrative';
import { hashReportDocumentModelContent } from '../reportGeneration/reportDocumentIdentity';
import { hasValidatedDeterministicScreening } from '../reportGeneration/deterministicScreeningValidation';
import { applySourceRoleTruthGuard } from '../audit/sourceRoleTruthGuard';
import { readRegulatoryFeasibilityScreen } from '../regulatoryFeasibility/frozen';
import { verifyWaterUseScreeningClaims } from '../audit/waterUseScreeningGuard';
import { summarizeQaSeverity } from '../audit/qaSeverityPolicy';
import {
  buildDecisionAnalysisInputEnvelopeV1,
  buildDecisionAnalysisSnapshotV1,
  DEFAULT_DECISION_ANALYSIS_VERSIONS,
} from './identity';
import {
  reportSedMaterialDependencyDrift,
  verifyReportSedMaterialDependencyManifest,
} from './sedContentIdentity';

export interface BuildDecisionAnalysisFromReportV1Args {
  siteId: string;
  report: SiteRagReport;
  document: ReportDocumentModelV1;
  sed: SiteEvidenceDocument;
  createdAt?: string;
  projectIntent?: ProjectIntent | null;
  intentRevision?: string | null;
  typedPreferences?: TypedPreferencesByType | null;
  powerStrategy?: 'grid-only' | 'btm-only' | 'hybrid' | 'islanded' | null;
  testFitAuthorityRef?: TestFitAuthorityRef | null;
  versions?: Partial<DecisionAnalysisVersionEnvelopeV1>;
  upstream?: readonly DecisionAnalysisUpstreamReferenceV1[];
}

const NARRATIVE_STAGES = new Set([
  'generateReportContent',
  'generateEnvironmentalReportContent',
  'generatePowerReportContent',
  'generateWaterReportContent',
  'generateTestFitContent',
  'generateInterconnectionContent',
  'generateSupplyChainReportContent',
  'generatePublicSentimentReportContent',
  'generateCustomBriefReportContent',
  'generateExecutiveSummaryReportContent',
  'generateEditorialNarrative',
]);

function stableId(prefix: string, value: unknown): string {
  return `${prefix}-${hashCanonicalJson(value).slice(0, 20)}`;
}

function containsBlockSeverity(value: unknown, visited = new Set<object>()): boolean {
  if (!value || typeof value !== 'object') return false;
  if (visited.has(value)) return false;
  visited.add(value);
  if (Array.isArray(value)) {
    return value.some(entry => containsBlockSeverity(entry, visited));
  }
  const row = value as Record<string, unknown>;
  return row.severity === 'block'
    || Object.values(row).some(entry => containsBlockSeverity(entry, visited));
}

function reportDomain(report: SiteRagReport): DecisionAnalysisDomain {
  const result = decisionAnalysisDomainSchema.safeParse(
    report.reportType ?? 'feasibility',
  );
  if (!result.success) {
    throw new Error(
      `Report type ${report.reportType ?? 'feasibility'} is presentation-only and cannot become a decision analysis.`,
    );
  }
  return result.data;
}

function modelAndPrompt(report: SiteRagReport): {
  model: string | null;
  promptVersion: string | null;
} {
  const calls = report.generationTelemetry?.calls ?? [];
  let model: string | null = null;
  for (let index = calls.length - 1; index >= 0; index -= 1) {
    const call = calls[index];
    if (call?.success && NARRATIVE_STAGES.has(call.stage)) {
      model = call.model;
      break;
    }
  }
  return {
    model,
    promptVersion: report.promptVersion
      ?? report.generationTelemetry?.promptVersion
      ?? null,
  };
}

function narrativeState(
  report: SiteRagReport,
  document: ReportDocumentModelV1,
  createdAt: string,
  deterministicValidated: boolean,
): DecisionAnalysisSemanticContentV1['narrative'] {
  // The withheld-narrative notice is the platform saying "not assessed", not
  // written analysis: a withheld `summary` freezes as the notice (so the
  // public edition and the complete-record appendix show it), and a withheld
  // leaf inside other content stays in place as a notice block. Neither may
  // count here, or a report whose only narrative was withheld (Financial
  // Viability has no narrative blocks, so `summary` is its only one) would
  // read as decision-ready.
  const isWrittenAnalysis = (text: string): boolean => {
    const trimmed = text.trim();
    return trimmed.length > 0 && trimmed !== WITHHELD_NARRATIVE_NOTICE;
  };
  const hasNarrative = Boolean(
    isWrittenAnalysis(document.summary)
    || document.sections.some(section => section.blocks.some(block => (
      block.kind === 'narrative' && isWrittenAnalysis(block.markdown)
    ))),
  );
  const provenance = modelAndPrompt(report);
  if (hasNarrative && deterministicValidated) {
    return { state: 'ready', generatedAt: createdAt, model: null, promptVersion: null,
      generationMethod: 'deterministic-source-screening', constructionVersion: report.deterministicScreeningValidation!.method };
  }
  if (
    !hasNarrative
    || !provenance.model
    || !provenance.promptVersion
  ) {
    return {
      state: 'failed',
      attemptedAt: createdAt,
      model: provenance.model,
      promptVersion: provenance.promptVersion,
      reason: !hasNarrative
        ? 'The frozen report document contains no written explanatory analysis.'
        : 'The frozen report does not prove both narrative model and prompt versions.',
    };
  }
  if (report.degradedMode) {
    return {
      state: 'degraded',
      generatedAt: createdAt,
      model: provenance.model,
      promptVersion: provenance.promptVersion,
      reason: report.degradedReason?.trim()
        || 'The source report marked its written narrative as degraded.',
    };
  }
  return {
    state: 'ready',
    generatedAt: createdAt,
    model: provenance.model,
    promptVersion: provenance.promptVersion,
  };
}

function evidenceSourceIds(value: EvidenceValue): string[] {
  return value.sourceIds;
}

function findingsFromReport(
  report: SiteRagReport,
): DecisionAnalysisFindingV1[] {
  return (report.structuredFindings ?? []).flatMap((finding, index) => {
    if (
      finding.impact !== 'positive'
      && finding.impact !== 'negative'
      && finding.impact !== 'neutral'
    ) return [];
    const sedPath = finding.source.trim();
    if (!sedPath || !finding.title.trim() || !finding.detail.trim()) return [];
    return [{
      id: stableId('finding', { index, finding }),
      title: finding.title,
      detail: finding.detail,
      impact: finding.impact,
      sedPaths: [sedPath],
      sourceIds: [],
      confidence: null,
    }];
  });
}

/**
 * A source risk row that cannot meet the analysis risk contract is EXCLUDED
 * and DISCLOSED as a warning, never repaired: no severity, trigger or
 * mitigation is inferred and the report row stays unchanged. One malformed
 * row (live 2026-10-13: reviewer JSON spilled into a severity) previously
 * failed the whole finished report. A repeated id keeps both risks: the later
 * row takes the first free `<id>-<n>` suffix. Valid rows with unique ids are
 * byte-identical to the previous mapping, so existing analyses keep their hashes.
 */
function risksFromReport(report: SiteRagReport): {
  risks: DecisionAnalysisRiskV1[];
  excluded: DecisionAnalysisSemanticContentV1['quality']['findings'];
} {
  const accepted: DecisionAnalysisRiskV1[] = [];
  const excluded: DecisionAnalysisSemanticContentV1['quality']['findings'] = [];
  const exclude = (index: number, label: unknown, id: string, fields: readonly string[]): void => {
    const name = typeof label === 'string' && label.trim() ? label.trim().slice(0, 80) : 'unlabelled';
    excluded.push({
      code: 'source-report-risk-excluded',
      severity: 'warning',
      message: `Source report risk ${index + 1} ("${name}", id ${id.slice(0, 80)}) was excluded from the decision`
        + ` analysis because its ${fields.join(', ')} ${fields.length === 1 ? 'is' : 'are'} missing or invalid.`
        + ' The report row is unchanged; no value was inferred.',
      sourceIds: [],
    });
  };
  for (const [index, entry] of ((report.risks ?? []) as readonly unknown[]).entries()) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      exclude(index, undefined, stableId('risk', { index, risk: entry }), ['row']);
      continue;
    }
    const risk = entry as Record<string, unknown>;
    const declaredId = (typeof risk.id === 'string' ? risk.id.trim() : '') || stableId('risk', { index, risk });
    // Built exactly as before; checked before it may enter the analysis.
    const row: Record<keyof DecisionAnalysisRiskV1, unknown> = {
      id: declaredId,
      label: risk.label,
      severity: risk.severity,
      trigger: (typeof risk.triggeringValue === 'string' ? risk.triggeringValue.trim() : '') || risk.impact,
      mitigation: risk.mitigation,
      sedPaths: [],
      sourceIds: risk.contributingDocIds ?? [],
      confidence: null,
    };
    const checked = decisionAnalysisRiskV1Schema.safeParse(row);
    if (!checked.success) {
      exclude(index, risk.label, declaredId,
        [...new Set(checked.error.issues.map(issue => String(issue.path[0] ?? 'row')))]);
      continue;
    }
    accepted.push(row as DecisionAnalysisRiskV1);
  }
  // Excluded rows claim no id. Suffixes avoid every accepted id, so the
  // re-keyed set is unique without renaming any first occurrence.
  const taken = new Set(accepted.map(risk => risk.id));
  const seen = new Set<string>();
  const risks = accepted.map(risk => {
    if (!seen.has(risk.id)) {
      seen.add(risk.id);
      return risk;
    }
    let suffix = 2;
    while (taken.has(`${risk.id}-${suffix}`)) suffix += 1;
    const id = `${risk.id}-${suffix}`;
    taken.add(id);
    return { ...risk, id };
  });
  return { risks, excluded };
}

function sourceIdentities(
  document: ReportDocumentModelV1,
  semanticSourceIds: readonly string[],
  upstream: readonly DecisionAnalysisUpstreamReferenceV1[],
): DecisionAnalysisSemanticContentV1['sources'] {
  const citations = new Map(document.citations.map(citation => [citation.id, citation]));
  const ids = new Set<string>([
    ...document.citations.map(citation => citation.id),
    ...semanticSourceIds,
  ]);
  const rows: DecisionAnalysisSemanticContentV1['sources'] = [...ids]
    .sort()
    .map(id => {
      const citation = citations.get(id);
      const retrievedAt = citation?.retrievedAt
        && Number.isFinite(Date.parse(citation.retrievedAt))
        ? new Date(citation.retrievedAt).toISOString()
        : undefined;
      return {
        id,
        kind: id.includes(':') ? id.split(':', 1)[0]! : 'source',
        label: citation?.label ?? id,
        ...(citation?.url ? { uri: citation.url } : {}),
        ...(retrievedAt ? { retrievedAt } : {}),
        authority: 'unclassified' as const,
      };
    });
  for (const reference of upstream) {
    if (ids.has(reference.id)) continue;
    ids.add(reference.id);
    rows.push({
      id: reference.id,
      kind: reference.kind,
      label: reference.id,
      contentHash: reference.contentHash,
      authority: 'derived',
    });
  }
  return rows;
}

function reportScore(
  report: SiteRagReport,
): DecisionAnalysisSemanticContentV1['score'] {
  if (
    !Number.isFinite(report.overallScore)
    || report.overallScore < 0
    || report.overallScore > 100
  ) return null;
  const components = (report.categories ?? []).flatMap(category => (
    Number.isFinite(category.score)
    && category.score >= 0
    && category.score <= 100
    && Number.isFinite(category.weight)
    && category.weight >= 0
      ? [{
          id: category.id,
          label: category.label,
          score: {
            value: category.score,
            minimum: 0,
            maximum: 100,
            higherIsBetter: true,
          },
          weight: category.weight,
          sedPaths: category.metrics.map(metric => metric.id),
          sourceIds: category.contributingDocIds ?? [],
        }]
      : []
  ));
  return {
    overall: {
      value: report.overallScore,
      minimum: 0,
      maximum: 100,
      higherIsBetter: true,
    },
    confidence: null,
    components,
  };
}

/**
 * Conservative bridge for the existing report generator. It freezes only
 * fields proven by the report/document/SED inputs and marks the semantic
 * quality or adequacy held whenever a required fact is absent. It never reads
 * a presentation or PDF artifact.
 */
export interface UnresolvedHighQualityReviewIssue {
  id: string;
  section: string;
  text: string;
  severity: 'high';
}

function normalizeQualityReviewIssueSeverity(raw: unknown): 'high' | 'medium' | 'low' {
  const s = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  if (s === 'high' || s === 'major' || s === 'critical' || s === 'block') return 'high';
  if (s === 'medium' || s === 'moderate' || s === 'warn' || s === 'warning') return 'medium';
  if (s === 'low' || s === 'minor' || s === 'info') return 'low';
  return 'high';
}

function isLikelyDiagnosticComplaintDirective(text: string | undefined, findingText: string): boolean {
  if (!text) return false;
  const trimmed = text.trim();
  if (!trimmed) return true;
  if (trimmed.toLowerCase() === findingText.trim().toLowerCase()) return true;
  return (
    /^In\s+[A-Za-z0-9_.[\]-]+(?:\s+and\s+[A-Za-z0-9_.[\]-]+)?(?:\s*\([^)]*\))?,\s+(?:the\s+text|Phase\s+\d+|the\s+narrative|the\s+section|it)\b/i.test(trimmed)
    || /^The\s+(?:Executive\s+Summary|report|section|subsection|narrative|text|table|study|summary|recommendation)\s+(?:repeats|states|claims|asserts|omits|lacks|fails\s+to|names|identifies|cites|describes|lists|incorrectly)\b.*?\b(?:without|rather\s+than|instead\s+of|which\s+contradicts|contradicting)\b/i.test(trimmed)
    || /\bwithout\s+(?:hedging\s+it\s+as|explicitly\s+stating|their\s+full\s+cumulative|qualifying\s+it\s+as|stating\s+their)\b/i.test(trimmed)
    || /\bare\s+stated\s+without\s+their\b/i.test(trimmed)
  );
}

function isFindingResolvedInApprovedFactualCorrections(
  report: Pick<SiteRagReport, 'approvedFactualCorrections' | 'appliedCorrectionsSummary'> & Partial<SiteRagReport>,
  identity: string,
  section: string,
  findingText: string,
): boolean {
  const normId = identity.trim().toLowerCase();
  const normSection = section.trim().toLowerCase();
  const normText = findingText.trim().toLowerCase();
  for (const [key, entry] of Object.entries(report.approvedFactualCorrections?.entries ?? {})) {
    if (entry.status !== 'active' || entry.lastEvaluationStatus !== 'resolved') continue;
    if (isLikelyDiagnosticComplaintDirective(entry.appliedDirective, findingText)) continue;
    if (key.trim().toLowerCase() === normId || (entry.identity ?? '').trim().toLowerCase() === normId) {
      return true;
    }
    if (
      normText.length > 0
      && (entry.section ?? '').trim().toLowerCase() === normSection
      && (entry.finding ?? '').trim().toLowerCase() === normText
    ) {
      return true;
    }
  }
  for (const item of report.appliedCorrectionsSummary?.items ?? []) {
    if (item.status !== 'resolved') continue;
    if (isLikelyDiagnosticComplaintDirective(item.directive, findingText)) continue;
    if ((item.identity ?? '').trim().toLowerCase() === normId) return true;
    if (
      normText.length > 0
      && (item.section ?? '').trim().toLowerCase() === normSection
      && (item.finding ?? '').trim().toLowerCase() === normText
    ) {
      return true;
    }
  }
  return false;
}

export function extractUnresolvedSanityFindings(
  report: Pick<SiteRagReport, 'sanityFindings' | 'approvedFactualCorrections' | 'appliedCorrectionsSummary'> & Partial<SiteRagReport>,
): SanityFinding[] {
  const narrativeTexts = collectNarrativeStrings(report);
  return (report.sanityFindings ?? []).filter(finding => {
    if (finding.id === EXECUTIVE_SUMMARY_TRACEABILITY_FINDING) return false;
    if (finding.resolution === 'ignore') return false;
    if ((finding.severity as string) === 'verified') return false;
    if (String(finding.category ?? '').endsWith('-verified')) return false;
    if (finding.category === 'false-positive') return false;
    if (isFalsePositiveQueueDurationFindingText(finding.finding ?? '', narrativeTexts)) return false;
    const identity = sanityFindingIdentity(finding);
    if (
      finding.resolution !== 'apply'
      && isFindingResolvedInApprovedFactualCorrections(report, identity, finding.section ?? '', finding.finding ?? '')
    ) {
      return false;
    }
    return (
      finding.severity === 'high'
      || finding.category === 'inconsistency'
      || finding.category === 'misleading'
      || finding.verificationStatus === 'contradicted'
      || String(finding.id ?? '').startsWith('sf-hold-')
    );
  });
}

export function extractUnresolvedHighQualityReviewIssues(
  report: Pick<
    SiteRagReport,
    'qualityReview' | 'sanityFindings' | 'qaFindings' | 'approvedFactualCorrections' | 'appliedCorrectionsSummary'
  > & Partial<SiteRagReport>,
): UnresolvedHighQualityReviewIssue[] {
  const reviewIssues = report.qualityReview?.issues ?? [];
  if (!Array.isArray(reviewIssues) || reviewIssues.length === 0) return [];
  const narrativeTexts = collectNarrativeStrings(report);
  const combinedNarrativeText = narrativeTexts.join('\n');
  const hasOutOfScopeWaterFindings = (report.qaFindings?.waterUseScreening?.findings ?? []).some(
    f => f.kind === 'water-band-out-of-scope-load',
  );
  const baseSanityFindings = (report.sanityFindings ?? []).filter(
    f => !isFalsePositiveQueueDurationFindingText(f.finding ?? '', narrativeTexts),
  );
  const existingSanityTexts = new Set(
    baseSanityFindings
      .map(f => (f.finding ?? '').trim().toLowerCase())
      .filter(Boolean),
  );
  const existingSanityIdentities = new Set(
    baseSanityFindings.map(f => sanityFindingIdentity(f).toLowerCase()),
  );
  const seenTexts = new Set<string>();
  const out: UnresolvedHighQualityReviewIssue[] = [];

  reviewIssues.forEach((issue, idx) => {
    const rawIssue = (issue ?? {}) as {
      issue?: unknown;
      description?: unknown;
      message?: unknown;
      severity?: unknown;
      section?: unknown;
      resolution?: unknown;
    };
    if (rawIssue.resolution === 'ignore') return;
    const severity = normalizeQualityReviewIssueSeverity(rawIssue.severity);
    if (severity !== 'high') return;
    const primaryText =
      typeof rawIssue.issue === 'string' && rawIssue.issue.trim().length > 0
        ? rawIssue.issue.trim()
        : typeof rawIssue.description === 'string' && rawIssue.description.trim().length > 0
          ? rawIssue.description.trim()
          : typeof rawIssue.message === 'string' && rawIssue.message.trim().length > 0
            ? rawIssue.message.trim()
            : '';
    if (!primaryText) return;
    if (
      isFalsePositiveQueueDurationFindingText(primaryText, narrativeTexts)
      || isReviewIssueAlreadyQualifiedInText(primaryText, combinedNarrativeText, report)
      || (/water-use screen check flagged out-of-scope load bindings/i.test(primaryText)
        && !hasOutOfScopeWaterFindings)
    ) {
      return;
    }
    const lowerText = primaryText.toLowerCase();
    if (existingSanityTexts.has(lowerText) || seenTexts.has(lowerText)) return;
    const section =
      typeof rawIssue.section === 'string' && rawIssue.section.trim().length > 0
        ? rawIssue.section.trim()
        : 'executiveSummary';
    const syntheticIdentity = sanityFindingIdentity({
      id: `qr-issue-${idx}`,
      category: 'inconsistency',
      section,
      finding: primaryText,
    });
    if (existingSanityIdentities.has(syntheticIdentity.toLowerCase())) return;
    seenTexts.add(lowerText);
    out.push({
      id: `qr-issue-${idx}`,
      section,
      text: primaryText,
      severity: 'high',
    });
  });

  return out;
}

export function buildDecisionAnalysisFromReportV1(
  args: BuildDecisionAnalysisFromReportV1Args,
): DecisionAnalysisSnapshotV1 {
  const report = args.report;
  const document = reportDocumentModelV1Schema.parse(args.document);
  if (document.reportId !== report.id) {
    throw new Error('Report document identity does not match the source report.');
  }
  if (document.siteId && document.siteId !== args.siteId) {
    throw new Error('Report document site identity does not match the analysis site.');
  }
  const domain = reportDomain(report);
  // The semantic bridge has the exact source, so verify it as well as the
  // full-body receipt. A metadata flag, another SED, or changed narrative can
  // never waive the ordinary model/reviewer provenance requirements.
  const deterministicValidated = hasValidatedDeterministicScreening(report)
    && document.qualityDecision.validationMethod === 'deterministic-source-screening'
    && report.deterministicScreeningValidation!.source.sedId === args.sed.id
    && report.deterministicScreeningValidation!.source.sedVersion === args.sed.version
    && report.deterministicScreeningValidation!.source.snapshotHash === hashCanonicalJson(args.sed);
  const noLocalStanceValidated = deterministicValidated
    && report.deterministicScreeningValidation!.method === 'source-bound-no-local-stance-v1';
  if (document.reportType !== domain) {
    throw new Error('Report document type does not match the decision-analysis domain.');
  }

  const createdAt = args.createdAt ?? report.createdAt;
  const versions: DecisionAnalysisVersionEnvelopeV1 = {
    analysisPolicy: withWaterSourceTruthPolicy(domain, withRegulatoryFeasibilityPolicy(domain,
      withAirAttainmentTruthPolicy(domain, withCurrentTruthReadinessPolicy(
        args.versions?.analysisPolicy && args.versions.analysisPolicy !== DEFAULT_DECISION_ANALYSIS_VERSIONS.analysisPolicy
          ? args.versions.analysisPolicy
          : `${DEFAULT_DECISION_ANALYSIS_VERSIONS.analysisPolicy}:${report.promptVersion ?? report.generationTelemetry?.promptVersion ?? 'legacy-unversioned'}`)))),
    factModel: args.versions?.factModel
      ?? DEFAULT_DECISION_ANALYSIS_VERSIONS.factModel,
    scorer: args.versions?.scorer
      ?? report.scoringThresholdsVersion
      ?? DEFAULT_DECISION_ANALYSIS_VERSIONS.scorer,
  };
  const typedPreferences = args.typedPreferences
    ?? report.typedPreferencesAtGen
    ?? report.typedPreferences
    ?? null;
  const powerStrategy = args.powerStrategy
    ?? typedPreferences?.['power-availability']?.btmStrategy
    ?? null;
  const authority = args.testFitAuthorityRef
    ?? report.testFitAuthorityRef
    ?? null;
  const upstream = [...(args.upstream ?? [])];
  const materialDependencies = report.sedMaterialDependenciesAtGen;
  if (materialDependencies !== undefined
    && !verifyReportSedMaterialDependencyManifest(materialDependencies, domain)) {
    throw new Error('Report material SED dependency manifest is invalid for its decision domain.');
  }
  const input = buildDecisionAnalysisInputEnvelopeV1({
    siteId: args.siteId,
    domain,
    sed: {
      id: args.sed.id,
      version: args.sed.version,
      document: args.sed,
    },
    intentSnapshot: args.projectIntent ?? report.projectIntentAtGen ?? null,
    intentRevision: args.intentRevision ?? null,
    typedPreferencesSnapshot: typedPreferences,
    powerStrategy,
    authority,
    versions,
    upstream,
    materialScope: materialDependencies?.scope,
    bindMaterialDependencies: materialDependencies !== undefined,
  });
  // An unstamped old report did not declare prompt versus full-SED scope.
  // Its envelope therefore retains its conservative historical currency.
  if (materialDependencies !== undefined && !reportSedMaterialDependencyDrift(materialDependencies,
    input.sed.materialDependencies).current) {
    throw new Error('Report material SED dependencies do not match the exact generation source.');
  }

  const findings = findingsFromReport(report);
  const { risks, excluded: excludedRiskFindings } = risksFromReport(report);
  // R14 — the frozen regulatory screen (feasibility only; validated, throws on
  // tamper). Null for legacy reports, so their analyses stay byte-identical.
  const regulatory = regulatoryFeasibilityPolicyApplies(domain)
    ? regulatoryFeasibilityDecisionRows(readRegulatoryFeasibilityScreen(report))
    : null;
  const gaps = (report.dataGaps ?? []).map((gap, index) => ({
    id: stableId('gap', { index, gap }),
    field: gap.field,
    reason: gap.reason,
    severity: gap.impact === 'critical'
      ? 'blocking' as const
      : gap.impact === 'significant'
        ? 'material' as const
        : 'minor' as const,
    wouldChangeDecision: gap.impact !== 'minor',
    recommendedAction: gap.recommendation,
    sedPaths: [gap.field],
    sourceIds: gap.providerId ? [gap.providerId] : [],
  }));
  const unknowns: DecisionAnalysisSemanticContentV1['unknowns'] = [
    ...gaps.map(gap => ({
      id: `unknown-${gap.id}`,
      field: gap.field,
      reason: gap.reason,
      materiality: gap.severity,
      sedPaths: gap.sedPaths,
      sourceIds: gap.sourceIds,
    })),
    ...Object.entries(document.evidence).flatMap(([field, value]) => (
      value.state === 'unknown'
        ? [{
            id: stableId('unknown', { field, value }),
            field,
            reason: value.reason,
            materiality: 'material' as const,
            sedPaths: [field],
            sourceIds: value.sourceIds,
          }]
        : []
    )),
    ...(regulatory?.unknowns ?? []),
  ];
  const conflicts: DecisionAnalysisSemanticContentV1['conflicts'] =
    Object.entries(document.evidence).flatMap(([field, value]) => {
      if (value.state !== 'conflict' || value.sourceIds.length === 0) return [];
      return [{
        id: stableId('conflict', { field, value }),
        field,
        description: value.reason,
        severity: 'high' as const,
        status: 'unresolved' as const,
        candidates: value.candidates.map((candidate: JsonValue) => ({
          value: candidate,
          sourceIds: value.sourceIds,
        })),
        resolution: null,
        sedPaths: [field],
      }];
    });

  /*
   * WITHHELD NARRATIVE (2026-09-18). The reviewer-voice gate withholds a
   * narrative leaf (null) and records its exact path. A withheld
   * recommendation title / key condition is NOT dropped (a Conditional GO
   * must never read with fewer conditions than the report stated) and NOT
   * back-filled with plausible text: it freezes as the explicit
   * "not assessed — treat as open" marker. Leaves without a withheld record
   * keep their exact pre-2026-09-18 handling.
   */
  const withheldLeaves = withheldNarrativeLeaves(report);
  const withheldPaths = new Set(withheldLeaves.map(leaf => leaf.path));
  // A later pass that re-indexes an array (dedup, reorder) can move a withheld
  // null off its recorded index, so a NULL leaf also matches a recorded leaf of
  // the same shape (`recommendations[*].title`). An empty STRING needs its exact
  // path: it can be legitimately empty.
  const shape = (path: string): string => path.replace(/\[\d+\]/g, '[*]');
  const withheldShapes = new Set(withheldLeaves.map(leaf => shape(leaf.path)));
  const withheldOr = (value: unknown, segments: (string | number)[]): unknown => {
    const path = formatReportLeafPath(segments);
    if (value === null || value === undefined) {
      return withheldPaths.has(path) || withheldShapes.has(shape(path)) ? WITHHELD_NARRATIVE_NOTICE : value;
    }
    return typeof value === 'string' && !value.trim() && withheldPaths.has(path)
      ? WITHHELD_NARRATIVE_NOTICE
      : value;
  };
  const recommendations = (report.recommendations ?? []).map((sourceRecommendation, index) => {
    const recommendation = {
      ...sourceRecommendation,
      title: withheldOr(sourceRecommendation.title, ['recommendations', index, 'title']) as string,
      description: withheldOr(sourceRecommendation.description, ['recommendations', index, 'description']) as string,
      rationale: withheldOr(sourceRecommendation.rationale, ['recommendations', index, 'rationale']) as string,
    };
    return {
      id: stableId('recommendation', { index, recommendation: sourceRecommendation }),
      title: recommendation.title,
      detail: [recommendation.description, recommendation.rationale]
        .filter(Boolean)
        // Two withheld leaves read as ONE marker, never a repeated notice.
        .filter((part, partIndex, parts) => part !== WITHHELD_NARRATIVE_NOTICE || parts.indexOf(part) === partIndex)
        .join(' ')
        || recommendation.title,
      priority: null,
      sedPaths: [],
      sourceIds: [],
    };
  });
  const canonicalDecision = resolveCanonicalReportDecisionProjection(report);
  const rawBindingConstraintLabels = [
    ...new Set([
      ...(report.executiveDecisionRollupAtGen?.bindingConstraints ?? []),
      ...canonicalDecision.bindingConstraints,
    ]),
  ];
  const bindingConstraints = rawBindingConstraintLabels.map((label, index) => ({
    id: stableId('constraint', { index, label }),
    kind: 'deterministic-verdict-cap',
    label,
    effect: 'cap' as const,
    detail: label,
    sedPaths: [],
    sourceIds: [],
  }));
  const reportConditions: DecisionAnalysisSemanticContentV1['conditions'] = (
    report.conclusion?.keyConditions ?? []
  ).flatMap((sourceCondition, index) => {
    const condition = withheldOr(sourceCondition, ['conclusion', 'keyConditions', index]) as string;
    const requirement = condition.trim();
    if (!requirement) return [];
    return [{
      id: stableId('condition', { index, requirement }),
      label: `Condition ${index + 1}`,
      requirement,
      status: 'open' as const,
      /*
       * Legacy conclusion conditions do not carry field/source provenance.
       * Keep those arrays explicitly empty rather than inventing evidence.
       */
      sedPaths: [],
      sourceIds: [],
    }];
  });
  // One open condition per 'approval required' screen row. They never stand in
  // for the report's own conditions: a conditional verdict that states none is
  // still inadequate (below).
  const conditions: DecisionAnalysisSemanticContentV1['conditions'] = [
    ...reportConditions,
    ...(regulatory?.conditions ?? []),
  ];

  const requiredSections = document.sections.filter(section => section.required);
  const completeRequiredSections = requiredSections.filter(section => section.blocks.length > 0);
  const requiredOmissions = document.omissions.filter(omission => omission.required);
  const coverageRatio = requiredSections.length > 0
    ? completeRequiredSections.length / requiredSections.length
    : 1;
  const blockingGapIds = gaps
    .filter(gap => gap.severity === 'blocking')
    .map(gap => gap.id);
  const unresolvedHighConflictIds = conflicts
    .filter(conflict => conflict.severity === 'high' && conflict.status === 'unresolved')
    .map(conflict => conflict.id);
  const conditionalWithoutConditions = !noLocalStanceValidated
    && report.overallStatus === 'amber'
    && canonicalDecision.band === 'conditional'
    && reportConditions.length === 0;
  const adequate = (
    coverageRatio === 1
    && requiredOmissions.length === 0
    && blockingGapIds.length === 0
    && unresolvedHighConflictIds.length === 0
    && !conditionalWithoutConditions
  );
  const inadequacyReasons = [
    ...(coverageRatio < 1 || requiredOmissions.length > 0
      ? ['One or more required semantic sections are unavailable.']
      : []),
    ...(blockingGapIds.length > 0 ? ['Blocking evidence gaps remain.'] : []),
    ...(unresolvedHighConflictIds.length > 0
      ? ['Unresolved high-severity evidence conflicts remain.']
      : []),
    ...(conditionalWithoutConditions
      ? ['The conditional report verdict does not provide explicit decision conditions.']
      : []),
  ];

  /*
   * P0b (RCA 2026-08-03) — severity provenance. `document.qualityDecision.findings`
   * is a flat string list that MIXES deterministic gate output (quality-hold
   * strings, error-severity sanity checks) with the reviewer's free-prose
   * issue texts. The old blanket `state !== 'pass' ⇒ block` mapping meant one
   * deterministic failure promoted every reviewer nitpick to a blocking
   * "deterministic" finding (Redhead: 1 real gate failure surfaced as 7).
   * Reviewer issues are already represented once below via the
   * `source-report-review` mapping with verdict-based severity, so they are
   * EXCLUDED here rather than double-listed at inflated severity.
   */
  const reviewerIssueMessages = new Set(
    (report.qualityReview?.issues ?? []).map(issue => `${issue.section}: ${issue.issue}`),
  );
  const qualityFindings: DecisionAnalysisSemanticContentV1['quality']['findings'] = [
    ...document.qualityDecision.findings
      .filter(message => !reviewerIssueMessages.has(message))
      .map(message => ({
        code: 'report-document-quality',
        severity: document.qualityDecision.state === 'pass'
          ? 'warning' as const
          : 'block' as const,
        message,
        sourceIds: [],
      })),
    ...(requiredOmissions.length > 0
      ? [{
          code: 'required-semantic-omission',
          severity: 'block' as const,
          message: `Required sections omitted: ${requiredOmissions.map(row => row.sectionId).join(', ')}.`,
          sourceIds: [],
        }]
      : []),
    ...(conditionalWithoutConditions
      ? [{
          code: 'conditional-conditions-missing',
          severity: 'block' as const,
          message: 'The source report is conditional but proves no explicit conditions.',
          sourceIds: [],
        }]
      : []),
    ...(report.qualityHoldFindings ?? []).map(message => ({
      code: 'source-report-quality-hold',
      severity: 'block' as const,
      message,
      sourceIds: [],
    })),
    ...(report.qualityReview?.issues ?? [])
      .filter((issue) => {
        const issueText = String(issue?.issue ?? '');
        if (!issueText.trim()) return false;
        const narrativeText = collectNarrativeStrings(report).join('\n');
        return !isReviewIssueAlreadyQualifiedInText(issueText, narrativeText, report);
      })
      .map(issue => ({
        code: 'source-report-review',
        severity: report.qualityReview?.verdict === 'pass'
          ? 'warning' as const
          : 'block' as const,
        message: `${issue.section}: ${issue.issue}`,
        sourceIds: [],
      })),
  ];
  // Complete loss of reviewed-summary claim traceability is a narrow
  // evidence gate, independent of the general advisory-review policy.
  const executiveTraceabilityFindings = (report.sanityFindings ?? [])
    .filter(finding => finding.id === EXECUTIVE_SUMMARY_TRACEABILITY_FINDING);
  for (const finding of executiveTraceabilityFindings) qualityFindings.push({
    code: 'executive-summary-traceability-block', severity: 'block',
    message: finding.finding, sourceIds: [],
  });
  const documentHashValid = hashReportDocumentModelContent(document) === document.contentHash;
  if (!documentHashValid) {
    qualityFindings.push({
      code: 'report-document-identity-invalid',
      severity: 'block',
      message: 'The frozen report document content hash does not match its semantic content.',
      sourceIds: [],
    });
  }
  /*
   * SEVERITY POLICY (2026-08-05) — only a demonstrable UNTRUTH fails the
   * report. The former blanket `containsBlockSeverity` scan failed a report
   * on ANY block-severity finding from ~8 detectors, several with systematic
   * false-positive modes, so no report could pass (live 2026-08-04: 29
   * blocks, of which 6 named present-and-populated sections, 4 called
   * distinct quantities a conflict, 2 flagged scope notes). Advisory
   * findings are surfaced as warnings so the analysis stays decision-current
   * with a visible caveat list. See qaSeverityPolicy for the tier rules.
   */
  const sourceRoleFindings = applySourceRoleTruthGuard(report, args.sed).qaFindings?.sourceRoleTruth?.findings ?? [];
  for (const finding of sourceRoleFindings) qualityFindings.push({
    code: 'source-role-truth-block', severity: 'block',
    message: `${finding.sectionId}: ${finding.message}`,
    sourceIds: (report.references ?? []).flatMap((reference, index) => {
      const documentId = reference.documentId ?? (reference.url?.startsWith('doc://') ? reference.url.slice(6) : undefined);
      const citationId = `citation-ref-${index + 1}`;
      return documentId && finding.documentIds?.includes(documentId) && document.citations.some(citation => citation.id === citationId) ? [citationId] : [];
    }),
  });
  // Re-evaluate the final body against the supplied frozen SED. A copied QA
  // bucket, favorable reviewer score or adequate workload cannot waive this
  // current deterministic source requirement. The source is the actual SED
  // snapshot, not an invented external citation or government authority.
  const waterFindings = verifyWaterUseScreeningClaims(report, args.sed, domain);
  const waterSourceHash = waterFindings.length ? hashCanonicalJson(args.sed) : null;
  const waterSourceId = waterSourceHash ? stableId('sed-water-source', {
    id: args.sed.id, version: args.sed.version, snapshotHash: waterSourceHash,
  }) : null;
  for (const finding of waterFindings) qualityFindings.push({
    code: 'source-water-truth-block', severity: 'block',
    message: `${finding.sectionId}: ${finding.message} Observed: ${finding.observed}. Expected: ${finding.expected}.${finding.excerpt ? ` Report text: ${finding.excerpt}` : ''}`,
    sourceIds: waterSourceId ? [waterSourceId] : [],
  });
  const unresolvedSanityFindings = extractUnresolvedSanityFindings(report);
  for (const finding of unresolvedSanityFindings) {
    qualityFindings.push({
      code: 'source-report-truth-violation',
      severity: 'block',
      message: `sanityFindings (${finding.section}): ${finding.finding}`,
      sourceIds: [],
    });
  }
  const unresolvedHighQualityReviewIssues = extractUnresolvedHighQualityReviewIssues(report);
  for (const issue of unresolvedHighQualityReviewIssues) {
    qualityFindings.push({
      code: 'source-report-truth-violation',
      severity: 'block',
      message: `qualityReview (${issue.section}): ${issue.text}`,
      sourceIds: [],
    });
  }
  const qaSeverity = summarizeQaSeverity(report.qaFindings);
  const qaHardGateFailed =
    qaSeverity.blocking.length > 0
    || unresolvedSanityFindings.length > 0
    || unresolvedHighQualityReviewIssues.length > 0;
  for (const violation of qaSeverity.blocking) {
    qualityFindings.push({
      code: 'source-report-truth-violation',
      severity: 'block',
      message: `${violation.detector}: ${violation.message}`,
      sourceIds: [],
    });
  }
  for (const advisory of qaSeverity.advisory) {
    qualityFindings.push({
      code: 'source-report-quality-advisory',
      severity: 'warning',
      message: `${advisory.detector}: ${advisory.message}`,
      sourceIds: [],
    });
  }
  if (!report.qualityReview && !deterministicValidated) {
    qualityFindings.push({
      code: 'source-report-review-missing',
      severity: 'block',
      message: 'The source report does not prove a final narrative-review verdict.',
      sourceIds: [],
    });
  }

  // AUDIT-ADVISORY (2026-08-22): truth violations no longer fail the
  // snapshot's hard gates, except explicit source-role, water-source and executive traceability blocks below.
  // Other audits travel as 'source-report-truth-violation'
  // block findings (the recommended audit), and the document mint's own hard
  // gates are artifact-only now. qaHardGateFailed remains computed for the
  // findings list above.
  const hardGatesPassed = (
    documentHashValid
    && document.qualityDecision.hardGatesPassed
    && sourceRoleFindings.length === 0
    && waterFindings.length === 0
    && executiveTraceabilityFindings.length === 0
  );
  /*
   * FAILED vs HELD — INTEGRITY vs QUALITY (2026-08-17).
   *
   * These are different events and the reader needs them told apart:
   *   failed — no trustworthy artifact exists. The content hash does not match
   *            its own semantic content, so nothing here can be relied upon.
   *   held   — an analysis WAS produced and is intact; a quality gate says it
   *            must be revised before it can carry a decision.
   *
   * The `!hardGatesPassed` test used to sit in the FAILED branch, ahead of the
   * held branch, so a truth-gate hold could never reach `held`. Live on Triple
   * Threat every one of six revisions read "failed" in the Accountability tab
   * while each had in fact produced a full document — a 118-148 word executive
   * summary and five populated sections — and `report.qualityHold` said
   * `needs-revision`. It also flattened real progress: quality stamps across
   * those six went 20 -> 13 -> 1 -> 4 -> 5 -> 3, all under one identical tag.
   *
   * Nothing becomes usable by this change: `held` and `failed` are both
   * non-ready, `canUseForDecision` stays false, and the precise cause still
   * travels in `readiness.reasons` as `hard-gate-failed`. The readiness mapping
   * (readiness.ts ~247) already routes `held` / `!hardGatesPassed` to a `held`
   * decision, so this is the single point that was misreporting.
   */
  const qualityState: DecisionAnalysisSemanticContentV1['quality']['state'] =
    report.degradedMode || document.qualityDecision.state === 'degraded'
      ? 'degraded'
      : !documentHashValid
        ? 'failed'
        /*
         * Genuine drafts outrank EVERY held condition: 'held' is decision-
         * eligible now (audit-advisory), so a draft-tier report carrying a
         * hold stamp must not be lifted into it. isDraft with a hold stamp
         * and no explicit tier is the hold era's export-bar mirror, not a
         * draft; drafts mint 'failed' (not final decision evidence).
         */
        : (report.isDraft && report.qualityHold !== 'needs-revision')
          || report.generationTier === 'draft'
          || document.qualityDecision.state === 'draft'
        ? 'failed'
        /*
         * The LLM reviewer's verdict is ADVISORY (2026-08-05). Deterministic
         * truth detectors own the pass/fail gate; the reviewer's composite
         * score is a prose-quality judgement that must not, on its own, make
         * an otherwise-true report unusable — the live 2026-08-04 run held at
         * 74/100 against a 75 threshold while its narrative was accurate.
         * Its verdict + issues still surface as warnings via
         * `source-report-review`, and a genuine falsehood it catches is
         * caught independently by the truth detectors above.
         * `report.qualityHold` is retained because it is now set ONLY for
         * truth violations (see resolveQualityHold).
         */
        : !hardGatesPassed
          || document.qualityDecision.state === 'failed'
          /*
           * Blocking truth findings (or the hold stamp they mint) keep the
           * snapshot's quality state at 'held' — the semantic schema's
           * invariant is "pass requires no blocking findings", and 'held' is
           * the honest description. Under the audit-advisory policy 'held' is
           * fully decision-ELIGIBLE (readiness.qualityReady includes it), so
           * this costs nothing at the verdict; it only keeps the stamp true.
           */
          || qaHardGateFailed
          || report.qualityHold === 'needs-revision'
          /*
           * The reviewer's needs-revision verdict mints its issues at BLOCK
           * severity above, and the schema forbids 'pass' with blocking
           * findings — so the verdict-only shape (no truth hold: exactly what
           * the truth-only hold policy produces when the review loop exhausts
           * below threshold) must also mint 'held', or the snapshot build
           * throws ZodError. 'held' is decision-eligible (audit-advisory),
           * so this costs nothing at the verdict.
           */
          || report.qualityReview?.verdict === 'needs-revision'
          ? 'held'
          /*
           * 'held' is decision-ELIGIBLE (audit-advisory), so it is reserved
           * for AUDIT causes. Structural/process defects — required sections
           * omitted, a conditional verdict without conditions, no reviewer
           * verdict at all — are NOT audit outcomes and must stay ineligible:
           * omissions/conditional mint 'failed' (incomplete artifact), a
           * missing reviewer verdict mints 'degraded' (mirrors the document
           * mint's not-run state).
           */
          : requiredOmissions.length > 0 || conditionalWithoutConditions
            ? 'failed'
            : !report.qualityReview && !deterministicValidated
              ? 'degraded'
              : 'pass';
  if (qualityState !== 'pass' && qualityFindings.length === 0) {
    qualityFindings.push({
      code: 'source-report-not-final',
      severity: 'block',
      message: 'The source report is not a final, quality-passed semantic input.',
      sourceIds: [],
    });
  }
  // After the not-final explanation, so an advisory exclusion never stands in
  // for it. A warning leaves the quality state unchanged.
  qualityFindings.push(...excludedRiskFindings);

  const score = noLocalStanceValidated ? null : reportScore(report);
  const allSourceIds = new Set<string>();
  const addSourceIds = (sourceIds: readonly string[]): void => {
    for (const sourceId of sourceIds) allSourceIds.add(sourceId);
  };
  for (const section of document.sections) {
    for (const block of section.blocks) {
      addSourceIds(block.sourceIds);
    }
  }
  for (const claim of document.claims) {
    addSourceIds(claim.sourceIds);
  }
  for (const value of Object.values(document.evidence)) {
    addSourceIds(evidenceSourceIds(value));
  }
  for (const finding of findings) {
    addSourceIds(finding.sourceIds);
  }
  for (const risk of risks) {
    addSourceIds(risk.sourceIds);
  }
  for (
    const row
    of [
      ...gaps,
      ...unknowns,
      ...conditions,
      ...recommendations,
      ...bindingConstraints,
    ]
  ) {
    addSourceIds(row.sourceIds);
  }
  for (const conflict of conflicts) {
    for (const candidate of conflict.candidates) {
      addSourceIds(candidate.sourceIds);
    }
  }
  for (const component of score?.components ?? []) {
    addSourceIds(component.sourceIds);
  }

  const semantic: DecisionAnalysisSemanticContentV1 = {
    title: document.title,
    summary: document.summary,
    sections: document.sections.map(section => ({
      id: section.id,
      sectionId: section.sectionId,
      title: section.title,
      group: section.group,
      role: section.role,
      required: section.required,
      blocks: structuredClone(section.blocks),
    })),
    omissions: structuredClone(document.omissions),
    sources: [
      ...sourceIdentities(document, [...allSourceIds].filter(id => id !== regulatory?.source.id), upstream),
      ...(waterSourceId && waterSourceHash ? [{
        id: waterSourceId, kind: 'sed-snapshot',
        label: `Frozen site evidence ${args.sed.id}, version ${args.sed.version}`,
        contentHash: waterSourceHash, authority: 'derived' as const,
      }] : []),
      ...(regulatory && (regulatory.unknowns.length > 0 || regulatory.conditions.length > 0) ? [regulatory.source] : []),
    ],
    citations: structuredClone(document.citations),
    claims: structuredClone(document.claims),
    evidence: structuredClone(document.evidence),
    findings,
    risks,
    gaps,
    unknowns,
    conflicts,
    conditions,
    recommendations,
    bindingConstraints,
    adequacy: {
      state: adequate ? 'adequate' : 'inadequate',
      coverageRatio,
      reason: adequate ? null : inadequacyReasons.join(' '),
      blockingGapIds,
      unresolvedHighConflictIds,
    },
    score,
    systemDecision: {
      band: noLocalStanceValidated ? 'withheld' : canonicalDecision.band,
      basis: 'deterministic',
      // A withheld summary is the notice, never a rationale for the band.
      rationale: canonicalDecision.rationale
        || report.conclusion?.recommendation?.trim()
        || (document.summary.trim() === WITHHELD_NARRATIVE_NOTICE ? '' : document.summary.trim())
        || `Frozen report status: ${report.overallStatus}.`,
      conditionIds: conditions.map(condition => condition.id),
      bindingConstraintIds: bindingConstraints.map(constraint => constraint.id),
    },
    narrative: narrativeState(report, document, createdAt, deterministicValidated),
    quality: {
      state: qualityState,
      hardGatesPassed,
      findings: qualityFindings,
      evaluatedAt: createdAt,
      policyVersion: versions.analysisPolicy,
      ...(deterministicValidated ? { validationMethod: 'deterministic-source-screening' as const,
        reviewerVerdict: noLocalStanceValidated ? 'not-run' as const : (report.qualityReview?.verdict ?? 'not-run' as const) } : {}),
    },
  };

  return buildDecisionAnalysisSnapshotV1({
    input,
    createdAt,
    semantic,
  });
}
